/* Cassaforte · cifratura e decifratura nel browser.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * PERCHE' LA CIFRATURA STA QUI E NON SUL SERVER
 * ─────────────────────────────────────────────────────────────────────────
 *
 * La tabella `personal_wallet` non ha alcun hook di cifratura: il server
 * scrive a database esattamente la stringa che riceve. E' voluto. Questa e'
 * una cassaforte personale, nessun bot la legge e nessuno deve poterla aprire
 * dal server: la password non viene salvata da nessuna parte, non viaggia in
 * rete e non esiste un percorso di recupero. Se si perde, quel wallet e'
 * perso.
 *
 * Da non confondere con la tabella `wallet` (schermata Wallet), che e' quella
 * operativa dei tradebot: li' la chiave la cifra il server con la sua
 * SECURITY_KEY e il browser NON deve toccarla. La distinzione e' scritta per
 * esteso in `src/pagine/wallet/ModuloWallet.tsx`.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * DUE FORMATI, E PERCHE'
 * ─────────────────────────────────────────────────────────────────────────
 *
 * 1. `U2FsdGVkX1…` (legacy) — quello scritto dal frontend precedente con
 *    `AES.encrypt(valore, password)` di crypto-js. E' il formato OpenSSL
 *    "Salted__": AES-256-CBC con chiave derivata da EVP_BytesToKey, cioe' MD5
 *    a UNA iterazione. Debole contro un attacco a dizionario su chi si
 *    procurasse il database, ma e' il formato in cui sono gia' scritte le
 *    righe esistenti: si legge, non si scrive piu'.
 *
 * 2. `sbv1.…` (corrente) — PBKDF2-SHA256 a 210.000 iterazioni piu' AES-GCM
 *    a 256 bit, tutto con WebCrypto, niente libreria. Due guadagni concreti:
 *    la derivazione costa ~sei ordini di grandezza in piu' per tentativo, e
 *    GCM e' autenticato, quindi una password sbagliata si riconosce dal tag
 *    invece che dal fatto che il testo decifrato "non sembra" UTF-8.
 *
 * La lettura sceglie da sola guardando il prefisso: un wallet salvato anni fa
 * si apre come sempre, con la sua password di allora.
 */

/* ==========================================================================
 * Errori: il motivo serve alla schermata, il messaggio all'utente
 * ======================================================================== */

/**
 * `password` e `formato` sono le due cose che l'utente puo' correggere, e
 * vanno distinte: davanti a un dato illeggibile, dirgli "password sbagliata"
 * lo manda a ripescare password che non useranno mai.
 *
 * `ambiente` e' il caso in cui manca `crypto.subtle`, cioe' la pagina non e'
 * in un contesto sicuro (http non locale). Non e' colpa di quello che ha
 * digitato: va detto, non mascherato da errore di password.
 */
export type MotivoErrore = 'password' | 'formato' | 'ambiente';

export class ErroreCifratura extends Error {
  readonly motivo: MotivoErrore;

  constructor(motivo: MotivoErrore, messaggio: string) {
    super(messaggio);
    this.name = 'ErroreCifratura';
    this.motivo = motivo;
  }
}

/* ==========================================================================
 * Riconoscimento del formato
 * ======================================================================== */

export type Formato = 'legacy' | 'corrente';

const PREFISSO_LEGACY = 'U2FsdGVkX1'; // "Salted__" in base64, il preambolo di crypto-js
const PREFISSO_CORRENTE = 'sbv1.';

export function formatoDi(testo: string): Formato | null {
  if (testo.startsWith(PREFISSO_CORRENTE)) return 'corrente';
  if (testo.startsWith(PREFISSO_LEGACY)) return 'legacy';
  return null;
}

/* ==========================================================================
 * Formato corrente: PBKDF2-SHA256 + AES-GCM, via WebCrypto
 * ======================================================================== */

/* 210.000 iterazioni e' la raccomandazione OWASP per PBKDF2-SHA256. Sul
   browser costa qualche decimo di secondo una volta sola, all'apertura o al
   salvataggio: non e' un'operazione che si ripete mentre si naviga. */
const ITERAZIONI = 210_000;
const BYTE_SALE = 16;
const BYTE_IV = 12; // 96 bit, la misura per cui GCM e' specificato

function subtle(): SubtleCrypto {
  /* Fuori da un contesto sicuro `crypto.subtle` e' semplicemente assente:
     l'accesso non tira un errore, restituisce undefined, e il primo await
     fallisce dieci righe dopo con un messaggio che non spiega niente. */
  const c = globalThis.crypto;
  if (!c || !c.subtle) {
    throw new ErroreCifratura(
      'ambiente',
      'Il browser non espone WebCrypto: la pagina deve essere servita in HTTPS (o da localhost).',
    );
  }
  return c.subtle;
}

/* `Uint8Array<ArrayBuffer>` e non il semplice `Uint8Array`: dalla 5.7 il tipo
   e' generico sul buffer e il caso generale include `SharedArrayBuffer`, che
   WebCrypto non accetta. Scriverlo qui evita di inseguire l'errore in ogni
   punto di chiamata. */
type Byte = Uint8Array<ArrayBuffer>;

async function derivaChiave(password: string, sale: Byte): Promise<CryptoKey> {
  const materiale = await subtle().importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return subtle().deriveKey(
    { name: 'PBKDF2', salt: sale, iterations: ITERAZIONI, hash: 'SHA-256' },
    materiale,
    { name: 'AES-GCM', length: 256 },
    false, // non estraibile: la chiave derivata non deve poter uscire da qui
    ['encrypt', 'decrypt'],
  );
}

function inBase64(byte: Uint8Array): string {
  let s = '';
  for (const b of byte) s += String.fromCharCode(b);
  return btoa(s);
}

function daBase64(testo: string): Byte {
  const s = atob(testo);
  const byte = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) byte[i] = s.charCodeAt(i);
  return byte;
}

/**
 * Cifra un valore. Il risultato e' `sbv1.` seguito da base64 di
 * `sale(16) || iv(12) || testo cifrato + tag(16)`: tutto quello che serve a
 * riaprirlo sta dentro la stringa, tranne la password.
 */
export async function cifra(testo: string, password: string): Promise<string> {
  if (!password) throw new ErroreCifratura('password', 'Serve una password.');

  const sale = crypto.getRandomValues(new Uint8Array(BYTE_SALE));
  const iv = crypto.getRandomValues(new Uint8Array(BYTE_IV));
  const chiave = await derivaChiave(password, sale);

  const cifrato = new Uint8Array(
    await subtle().encrypt({ name: 'AES-GCM', iv }, chiave, new TextEncoder().encode(testo)),
  );

  const pacchetto = new Uint8Array(sale.length + iv.length + cifrato.length);
  pacchetto.set(sale, 0);
  pacchetto.set(iv, sale.length);
  pacchetto.set(cifrato, sale.length + iv.length);

  return PREFISSO_CORRENTE + inBase64(pacchetto);
}

async function decifraCorrente(testo: string, password: string): Promise<string> {
  let pacchetto: Byte;
  try {
    pacchetto = daBase64(testo.slice(PREFISSO_CORRENTE.length));
  } catch {
    throw new ErroreCifratura('formato', 'Il dato salvato non è leggibile: base64 non valido.');
  }
  if (pacchetto.length <= BYTE_SALE + BYTE_IV) {
    throw new ErroreCifratura('formato', 'Il dato salvato è troncato.');
  }

  const sale = new Uint8Array(pacchetto.subarray(0, BYTE_SALE));
  const iv = new Uint8Array(pacchetto.subarray(BYTE_SALE, BYTE_SALE + BYTE_IV));
  const cifrato = new Uint8Array(pacchetto.subarray(BYTE_SALE + BYTE_IV));
  const chiave = await derivaChiave(password, sale);

  try {
    const chiaro = await subtle().decrypt({ name: 'AES-GCM', iv }, chiave, cifrato);
    return new TextDecoder().decode(chiaro);
  } catch {
    /* GCM non distingue "password sbagliata" da "dato manomesso": in entrambi
       i casi il tag non torna. Fra le due, quella che capita davvero e' la
       password. */
    throw new ErroreCifratura('password', 'Password errata.');
  }
}

/* ==========================================================================
 * Formato legacy: crypto-js, solo in lettura
 * ======================================================================== */

/**
 * crypto-js arriva con un `import()` dinamico e non dall'alto del file: e' una
 * libreria di una quarantina di KB che serve soltanto ad aprire i wallet
 * salvati dal frontend precedente. Chi non ne ha non la scarica nemmeno.
 */
async function decifraLegacy(testo: string, password: string): Promise<string> {
  const { default: CryptoJS } = await import('crypto-js');

  let chiaro: string;
  try {
    chiaro = CryptoJS.AES.decrypt(testo, password).toString(CryptoJS.enc.Utf8);
  } catch {
    /* Con la password sbagliata i byte decifrati non sono UTF-8 valido e la
       conversione tira "Malformed UTF-8 data". E' il modo normale in cui
       questo formato dice di no. */
    throw new ErroreCifratura('password', 'Password errata.');
  }

  /* L'altro modo in cui dice di no: nessun errore e stringa vuota. Un valore
     vuoto non e' comunque un dato utile da mostrare. */
  if (!chiaro) throw new ErroreCifratura('password', 'Password errata.');
  return chiaro;
}

/* ==========================================================================
 * Ingresso unico
 * ======================================================================== */

/** Decifra scegliendo il formato dal prefisso. */
export async function decifra(testo: string, password: string): Promise<string> {
  const formato = formatoDi(testo);
  if (formato === 'corrente') return decifraCorrente(testo, password);
  if (formato === 'legacy') return decifraLegacy(testo, password);
  throw new ErroreCifratura(
    'formato',
    "Questo valore non è stato cifrato dalla cassaforte: non c'è niente da aprire.",
  );
}

/**
 * Cifra e riapre subito, controllando di aver ritrovato l'originale.
 *
 * Serve prima del salvataggio, e non e' zelo: quello che va a database e'
 * l'unica copia: se fosse illeggibile non lo scoprirebbe nessuno fino al
 * giorno in cui serve la chiave, cioe' il giorno peggiore. Costa una
 * derivazione in piu' su un'operazione che si fa una volta per wallet.
 */
export async function cifraVerificando(testo: string, password: string): Promise<string> {
  const cifrato = await cifra(testo, password);
  const riletto = await decifra(cifrato, password);
  if (riletto !== testo) {
    throw new ErroreCifratura(
      'formato',
      'Controllo di rilettura fallito: il dato cifrato non si riapre uguale. Non è stato salvato niente.',
    );
  }
  return cifrato;
}
